// Traduce las correcciones que pide el agente ("dice X, debería decir Y") a las
// `ediciones` por índice que ya entiende el backend de corregir voz.
//
// El agente pide por TEXTO a propósito: un modelo no produce índices de un array de
// 300 palabras de forma confiable, y el contrato de Creaciones (índice + texto) se
// diseñó para una pantalla donde el usuario toca la palabra. Acá la búsqueda se hace
// contra el transcript COMPLETO del asset, en tiempos del archivo, porque el backend opera
// sobre el asset entero (el de `get_clip_transcript` viene filtrado a la ventana visible y
// en segundos de timeline: usarlo corre todos los tiempos en un clip recortado).
//
// Límite conocido: el backend descarta una edición con texto vacío
// (`segmentosAReemplazar`), así que NO se puede borrar una palabra. Por eso una
// corrección que cambia la cantidad de palabras no se aplica a ciegas —dos palabras →
// una dejaría la segunda sonando— y vuelve al agente con el motivo, para que la pida
// palabra por palabra. Levantar ese límite es un cambio de contrato del backend.
import { esHablada, type EdicionLab, type PalabraLab } from "./transcript";

export interface Correccion {
  dice: string;
  deberia_decir: string;
}

export interface CorreccionAplicada extends Correccion {
  veces: number;
}

export interface CorreccionNoResuelta extends Correccion {
  motivo: string;
}

export interface ResultadoDeResolucion {
  ediciones: EdicionLab[];
  aplicadas: CorreccionAplicada[];
  noResueltas: CorreccionNoResuelta[];
}

const PUNTUACION_INICIAL = /^[^\p{L}\p{N}]+/u;
const PUNTUACION_FINAL = /[^\p{L}\p{N}]+$/u;

/** La palabra sin la puntuación de alrededor (las mayúsculas y los acentos quedan). */
const sinPuntuacion = (t: string) => t.trim().replace(PUNTUACION_INICIAL, "").replace(PUNTUACION_FINAL, "");
/** Lo comparable de una palabra: sin puntuación ni mayúsculas. */
const clave = (t: string) => sinPuntuacion(t).toLowerCase();
const tokens = (frase: string) => frase.trim().split(/\s+/).filter(Boolean);

export function resolverCorrecciones(palabras: PalabraLab[], correcciones: Correccion[]): ResultadoDeResolucion {
  const hablados: number[] = [];
  palabras.forEach((p, i) => {
    if (esHablada(p)) hablados.push(i);
  });
  const claves = hablados.map((i) => clave(palabras[i].text));

  const porIndice = new Map<number, string>();
  const aplicadas: CorreccionAplicada[] = [];
  const noResueltas: CorreccionNoResuelta[] = [];

  for (const c of correcciones) {
    const buscados = tokens(c.dice).map(clave).filter(Boolean);
    const nuevos = tokens(c.deberia_decir);
    if (buscados.length === 0 || nuevos.length === 0) {
      noResueltas.push({ ...c, motivo: "falta el texto que dice o el que debería decir" });
      continue;
    }
    if (buscados.join(" ") === nuevos.map(clave).join(" ")) {
      noResueltas.push({ ...c, motivo: "es igual a lo que ya dice" });
      continue;
    }
    if (buscados.length !== nuevos.length) {
      noResueltas.push({
        ...c,
        motivo: `necesita la misma cantidad de palabras: "${c.dice}" son ${buscados.length} y "${c.deberia_decir}" son ${nuevos.length}. Pedila palabra por palabra.`,
      });
      continue;
    }

    // Todas las apariciones, sin solaparse. `insertadas` cuenta las palabras que esta
    // corrección efectivamente puso: si otra ya había tomado ese índice, no se pisa y esta
    // no se reporta como aplicada — el usuario recibiría un "listo" por algo que no pasó.
    let veces = 0;
    let insertadas = 0;
    for (let k = 0; k + buscados.length <= claves.length; ) {
      const coincide = buscados.every((b, j) => claves[k + j] === b);
      if (!coincide) {
        k += 1;
        continue;
      }
      veces += 1;
      buscados.forEach((_b, j) => {
        const indice = hablados[k + j];
        const original = palabras[indice].text.trim();
        const prefijo = original.match(PUNTUACION_INICIAL)?.[0] ?? "";
        const sufijo = original.match(PUNTUACION_FINAL)?.[0] ?? "";
        // La puntuación la pone la palabra original, no el reemplazo: "gamuza," sobre
        // "gamula," daría "gamuza,," y eso es lo que se sintetiza y se guarda.
        const texto = `${prefijo}${sinPuntuacion(nuevos[j])}${sufijo}`;
        // Una palabra que queda igual no viaja: el backend regenera la oración entera
        // con lo que cambió, y lo demás lo lee del transcript.
        if (texto === original) return;
        if (porIndice.has(indice)) return;
        porIndice.set(indice, texto);
        insertadas += 1;
      });
      k += buscados.length;
    }
    if (veces === 0) {
      noResueltas.push({ ...c, motivo: "no aparece en el transcript" });
      continue;
    }
    if (insertadas === 0) {
      noResueltas.push({ ...c, motivo: "choca con otra corrección sobre la misma palabra" });
      continue;
    }
    aplicadas.push({ ...c, veces });
  }

  const ediciones = [...porIndice.entries()]
    .map(([indice, texto]) => ({ indice, texto }))
    .sort((a, b) => a.indice - b.indice);
  return { ediciones, aplicadas, noResueltas };
}
